import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import {
  HiOutlineTrash,
  HiOutlineShoppingBag,
  HiOutlineCash,
  HiOutlineUser,
  HiOutlineBell,
  HiOutlineTruck,
  HiOutlineUserGroup,
  HiOutlineCurrencyDollar,
} from "react-icons/hi";
import { markNotificationAsRead } from "../../services/notification.service";

const Notification = ({ notifications, onNotificationsViewed }) => {
  const [localNotifications, setLocalNotifications] = useState(notifications);

  // Update local state when props change
  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);

  const getNotificationDetails = (title, isDeleted = false) => {
    const lowerTitle = title.toLowerCase();

    if (lowerTitle.includes("sale")) {
      return {
        icon: isDeleted ? (
          <HiOutlineTrash className='text-red-500' />
        ) : (
          <HiOutlineShoppingBag className='text-green-500' />
        ),
        singular: "Sale",
        plural: "Sales",
        action: isDeleted ? "deleted" : "added",
      };
    } else if (lowerTitle.includes("purchase")) {
      return {
        icon: isDeleted ? (
          <HiOutlineTrash className='text-red-500' />
        ) : (
          <HiOutlineShoppingBag className='text-blue-500' />
        ),
        singular: "Purchase",
        plural: "Purchases",
        action: isDeleted ? "deleted" : "added",
      };
    } else if (lowerTitle.includes("payment")) {
      return {
        icon: isDeleted ? (
          <HiOutlineTrash className='text-red-500' />
        ) : (
          <HiOutlineCash className='text-yellow-500' />
        ),
        singular: "Payment",
        plural: "Payments",
        action: isDeleted ? "deleted" : "added",
      };
    } else if (lowerTitle.includes("customer")) {
      return {
        icon: isDeleted ? (
          <HiOutlineTrash className='text-red-500' />
        ) : (
          <HiOutlineUser className='text-purple-500' />
        ),
        singular: "Customer",
        plural: "Customers",
        action: isDeleted ? "deleted" : "added",
      };
    } else if (lowerTitle.includes("supplier")) {
      return {
        icon: isDeleted ? (
          <HiOutlineTrash className='text-red-500' />
        ) : (
          <HiOutlineTruck className='text-orange-500' />
        ),
        singular: "Supplier",
        plural: "Suppliers",
        action: isDeleted ? "deleted" : "added",
      };
    } else if (lowerTitle.includes("employee")) {
      return {
        icon: isDeleted ? (
          <HiOutlineTrash className='text-red-500' />
        ) : (
          <HiOutlineUserGroup className='text-indigo-500' />
        ),
        singular: "Employee",
        plural: "Employees",
        action: isDeleted ? "deleted" : "added",
      };
    } else if (lowerTitle.includes("expense")) {
      return {
        icon: isDeleted ? (
          <HiOutlineTrash className='text-red-500' />
        ) : (
          <HiOutlineCurrencyDollar className='text-red-400' />
        ),
        singular: "Expense",
        plural: "Expenses",
        action: isDeleted ? "deleted" : "added",
      };
    }

    return {
      icon: isDeleted ? (
        <HiOutlineTrash className='text-red-500' />
      ) : (
        <HiOutlineBell className='text-gray-500' />
      ),
      singular: title,
      plural: title + "s",
      action: isDeleted ? "deleted" : "added",
    };
  };
  const handleNotificationClick = useCallback(
    async (notification) => {
      console.log("Clicked notification ID:", notification._id); // Debug
      if (!notification.isRead) {
        try {
          // Verify the ID structure before sending
          if (!notification._id || notification._id.length !== 24) {
            throw new Error(`Invalid notification ID: ${notification._id}`);
          }

          const updated = await markNotificationAsRead(
            notification.businessId,
            notification._id
          );

          console.log("Updated notification:", updated); // Debug

          setLocalNotifications((prev) =>
            prev.map((n) =>
              n._id === notification._id ? { ...n, isRead: true } : n
            )
          );

          onNotificationsViewed(1);
        } catch (error) {
          console.error("Mark read failed:", {
            error: error.message,
            notificationId: notification._id,
            businessId: notification.businessId,
          });
        }
      }
    },
    [onNotificationsViewed]
  );

  // Group notifications by type, action (added/deleted), and day
  const groupedNotifications = localNotifications.reduce(
    (acc, notification) => {
      const day = moment(notification.createdAt).format("YYYY-MM-DD");
      const today = moment().format("YYYY-MM-DD");
      const yesterday = moment().subtract(1, "days").format("YYYY-MM-DD");

      let dayLabel;
      if (day === today) {
        dayLabel = "Today";
      } else if (day === yesterday) {
        dayLabel = "Yesterday";
      } else {
        dayLabel = moment(notification.createdAt).format("MMM D");
      }

      if (!acc[dayLabel]) {
        acc[dayLabel] = {};
      }

      const isDeleted =
        notification.status === "deleted" ||
        notification.title.toLowerCase().includes("deleted");

      const { singular } = getNotificationDetails(
        notification.title,
        isDeleted
      );
      const notificationType = singular;
      const notificationAction = isDeleted ? "deleted" : "added";

      const groupKey = `${notificationType}-${notificationAction}`;

      if (!acc[dayLabel][groupKey]) {
        acc[dayLabel][groupKey] = {
          items: [],
          isDeleted,
          type: notificationType,
        };
      }

      acc[dayLabel][groupKey].items.push(notification);

      return acc;
    },

    {}
  );

  return (
    <div className='max-h-[70vh] overflow-y-auto'>
      {Object.keys(groupedNotifications).length ? (
        Object.entries(groupedNotifications).map(([day, dayNotifications]) => (
          <div key={day} className='mb-4'>
            <div className='text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-2'>
              {day}
            </div>
            {Object.entries(dayNotifications).map(([groupKey, group]) => {
              const { items, isDeleted, type } = group;
              const isGrouped = items.length > 1;
              const firstItem = items[0];
              const { icon, singular, plural, action } = getNotificationDetails(
                type,
                isDeleted
              );

              return (
                <div
                  key={`${day}-${groupKey}`}
                  className={`flex items-start p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer mb-1 ${
                    isGrouped ? "bg-gray-50 dark:bg-gray-800" : ""
                  } ${
                    !firstItem.isRead ? "bg-blue-50 dark:bg-blue-900/30" : ""
                  }`}
                  onClick={() => handleNotificationClick(firstItem)}
                >
                  <div className='flex-shrink-0 mt-1 mr-3'>{icon}</div>
                  <div className='flex-1 min-w-0'>
                    {isGrouped ? (
                      <>
                        <p className='text-sm font-medium text-gray-900 dark:text-white'>
                          {items.length} {items.length > 1 ? plural : singular}{" "}
                          {action} {day.toLowerCase()}
                        </p>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                          {items.length} {type.toLowerCase()} activities
                        </p>
                      </>
                    ) : (
                      <>
                        <div className='flex justify-between items-start'>
                          <p className='text-sm font-medium text-gray-900 dark:text-white'>
                            {firstItem.title}
                          </p>
                          <span className='text-xs text-gray-500 dark:text-gray-400'>
                            {moment(firstItem.createdAt).fromNow()}
                          </span>
                        </div>
                        <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                          {firstItem.description}
                        </p>
                        {/* {firstItem.user && (
                          <div className='flex items-center mt-2'>
                            <UserImage
                              avatar={firstItem.user.profilePicture}
                              size='xs'
                            />
                            <span className='ml-2 text-xs text-gray-500 dark:text-gray-400'>
                              {firstItem.user.firstName}{" "}
                              {firstItem.user.lastName}
                            </span>
                          </div>
                        )} */}
                      </>
                    )}
                  </div>
                  {!firstItem.isRead && (
                    <div className='ml-2 w-2 h-2 rounded-full bg-blue-500'></div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      ) : (
        <div className='flex flex-col items-center justify-center p-6 text-center'>
          <HiOutlineBell className='h-10 w-10 text-gray-400 mb-2' />
          <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>
            No notifications yet
          </p>
          <p className='text-xs text-gray-400 dark:text-gray-500'>
            We'll notify you when something arrives
          </p>
        </div>
      )}
    </div>
  );
};

export default Notification;
